import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Input, Tooltip, message, AutoComplete, Select } from "antd";
import { SettingOutlined, SyncOutlined, CheckOutlined, ThunderboltOutlined } from "@ant-design/icons";
import axiosHttp from "../../appRedux/services/axiosCommon";
import AIGeneratorConfig from "./AIGeneratorConfig";

const { TextArea } = Input;
const MAX_HISTORY = 10;

const PREDEFINED_PROMPTS = {
    title: "Generate a concise, compelling SEO Meta Title (under 60 characters).",
    description: "Generate a persuasive SEO Meta Description (under 160 characters).",
    keywords: "Generate a comma-separated list of 5-8 highly relevant SEO keywords.",
    "og:title": "Generate an engaging Open Graph Title for social sharing.",
    "og:description": "Generate an engaging Open Graph Description for social sharing."
};
const DEFAULT_BULK_META_CONFIG = {
  systemPrompt: `You are an expert SEO copywriter specializing in online education content.

Your task is to generate a meta title and meta description for an online course page.

Tone rules:
- Always write in second person ("you", "your", "master", "build", "learn")
- Lead with the benefit or outcome, not the course name
- Be specific — reference the actual skills and tools involved
- Never start the description with the course title
- Write for a learner scanning search results, not for an academic audience

SEO rules:
- Meta title: 50–60 characters. Target the broader category keyword + core skill. The course title is context, not the keyword.
- Meta description: 140–155 characters. Lead with the primary keyword naturally in the first sentence. Use 1–2 skills from the provided skills list as secondary signals. End with an action-oriented phrase.
- Do not stuff keywords. One primary, one or two secondary — woven naturally into readable copy.
- Prioritize search intent over cleverness. A learner searching "WordPress theme development course" must feel this result is exactly what they need.

Differentiation rules:
- The skills list is your primary differentiator. Two courses in the same category must produce different descriptions if their skills differ.
- Never produce generic copy that could apply to any course in the category. Every output must be specific enough that removing the skills would make it meaningless.

Respond only with valid JSON. No markdown, no preamble, no explanation outside the JSON object.`,
  userPrompt: `Generate SEO meta tags for the following course.

---
Title: {{title}}
Category: {{category}}
Level / Target Audience: {{levelContext}}
What you'll learn (outcomes): {{out_come}}
Skills: {{skills}}
Description: {{description}}
---

Respond with this exact JSON structure:

{
  "meta": [
    { "name": "title", "content": "..." },
    { "name": "description", "content": "..." },
    { "name": "og:title", "content": "..." },
    { "name": "og:description", "content": "..." }
  ]
}

Rules:
- meta title: 50–60 characters
- meta description: 140–155 characters
- og:title and og:description can be slightly more engaging in tone but must stay within the same character limits
- do not include any text outside the JSON object
- CRITICAL: Start your response directly with '{'. No conversational text before the JSON.`,
  contextFields: ["title", "description", "out_come", "category", "skills", "level", "levelContext"]
};

const pulseKeyframes = `
@keyframes aiPulse {
  0% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(1.02); }
  100% { opacity: 1; transform: scale(1); }
}
`;

const getHistoryKey = (category, fieldName) => `ai_prompt_history_${category}_${fieldName}`;

const SingleFieldAIGenerator = ({
    fieldName = "",
    fieldLabel = "Field",
    currentValue = "",
    onApply,
    courseId,
    category = "SEO",
    // New Props for Bulk/Multi handling
    mode = "single",
    activeMetaKeys = [],
    defaultSelectedKeys = [],
    buttonComponent = null
}) => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [configVisible, setConfigVisible] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [hasGenerated, setHasGenerated] = useState(false);
    
    // Config state
    const [configData, setConfigData] = useState(null);
    const [isConfigLoaded, setIsConfigLoaded] = useState(false);
    
    // Prompt state
    const [inlinePrompt, setInlinePrompt] = useState("");
    const [promptHistory, setPromptHistory] = useState([]);
    const [selectedFields, setSelectedFields] = useState([]);
    
    // Output state
    const [generatedText, setGeneratedText] = useState("");

    const loadPromptHistory = () => {
        try {
            const saved = localStorage.getItem(getHistoryKey(category, fieldName));
            if (saved) {
                return JSON.parse(saved);
            }
        } catch (e) {
            console.error("Failed to load prompt history:", e);
        }
        return [];
    };

    const savePromptToHistory = (prompt) => {
        if (!prompt || !prompt.trim()) return;
        const trimmed = prompt.trim();
        const history = loadPromptHistory();
        const filtered = history.filter(h => h !== trimmed);
        filtered.unshift(trimmed);
        const newHistory = filtered.slice(0, MAX_HISTORY);
        localStorage.setItem(getHistoryKey(category, fieldName), JSON.stringify(newHistory));
        setPromptHistory(newHistory);
    };

    const lastFieldRef = useRef("");

    // Load configs
    useEffect(() => {
        if (isModalVisible && !isConfigLoaded) {
            loadConfig();
        }
        if (isModalVisible) {
            setPromptHistory(loadPromptHistory());
        }
    }, [isModalVisible, isConfigLoaded]);

    // Initialize prompt and default values only when modal opens
    useEffect(() => {
        if (isModalVisible) {
            // Only set/reset if this is a fresh open or field changed
            if (lastFieldRef.current !== fieldName) {
                setGeneratedText(mode === "single" ? currentValue : "");
                setHasGenerated(false);
                
                setInlinePrompt("");
                lastFieldRef.current = fieldName;
            }

            if (mode === "bulk") {
                setSelectedFields(defaultSelectedKeys.length > 0 ? defaultSelectedKeys : activeMetaKeys);
            } else {
                setSelectedFields([fieldName].filter(Boolean));
            }
        } else {
            // Reset when closed so it can re-init next time
            lastFieldRef.current = "";
        }
    }, [isModalVisible, mode, fieldName]); // Reduced dependencies to avoid accidental resets

    const loadConfig = async () => {
        try {
            const response = await axiosHttp.get(`/v2/admin/ai/config?category=${category}`);
            if (response.data && response.data.status && response.data.results) {
                setConfigData(response.data.results);
                setIsConfigLoaded(true);
            } else {
                // If it fails, we still allow generation using default fallback params in backend
                setConfigData({});
                setIsConfigLoaded(true);
            }
        } catch (error) {
            console.error("Error loading AI config:", error);
            setConfigData({});
            setIsConfigLoaded(true);
        }
    };

    const handleSettingsSaved = (newConfig) => {
        setConfigData(newConfig);
    };

    const handleGenerate = async () => {
        if (!courseId) {
            message.error("Course ID is missing.");
            return;
        }
        
        const finalPrompt = inlinePrompt.trim() || `Generate content for ${fieldLabel}`;
        const targetKeys = selectedFields.filter(Boolean);
        if (targetKeys.length === 0) {
            message.error("Please select at least one target field.");
            return;
        }
        
        setIsGenerating(true);
        setGeneratedText("");
        
        try {
            let systemPrompt = configData?.systemPrompt || DEFAULT_BULK_META_CONFIG.systemPrompt;
            let userPrompt = configData?.userPrompt || DEFAULT_BULK_META_CONFIG.userPrompt;
            const contextFields =
                configData?.contextFields && configData.contextFields.length > 0
                    ? configData.contextFields
                    : DEFAULT_BULK_META_CONFIG.contextFields;

            let specificInstructions = "";

            if (mode === "single") {
                // Simplify system prompt to prevent conflicting instructions
                if (category === "Bulk Meta Tags") {
                    systemPrompt = `You are an expert SEO copywriter specializing in online education content. Your task is to generate highly optimized, concise, and relevant SEO metadata.
- If generating a title, strictly keep it between 50-60 characters.
- If generating a description, strictly keep it between 140-155 characters.`;
                }
                
                // Override user prompt for single generation to save tokens
                const defaultSinglePrompt = PREDEFINED_PROMPTS[fieldName] || `Generate a concise, compelling SEO ${fieldLabel}.`;
                userPrompt = finalPrompt && finalPrompt !== "" ? finalPrompt : defaultSinglePrompt;
                
                specificInstructions = `\n\nCRITICAL: The user is requesting only the '${fieldName}' tag.
Return ONLY a valid JSON object strictly containing this key: "${fieldName}".
Do not include any top-level wrappers like {"meta": [...]}. Example: {"${fieldName}": "your text here"}`;
            } else {
                if (finalPrompt && finalPrompt !== "") {
                    userPrompt += `\n\nADDITIONAL USER INSTRUCTIONS:\n${finalPrompt}`;
                }
            }

            const baseInstructions = (configData?.internalInstructions || "").trim();
            const cInstructions = [baseInstructions, specificInstructions].filter(Boolean).join("\n\n");

            const payload = {
                courseId,
                category,
                mode: "json",
                output: {
                    format: "json",
                    keys: mode === "single" ? [fieldName] : []
                },
                systemPrompt,
                userPrompt,
                contextFields,
                contextInstructions: cInstructions,
                modelOverride: configData?.model || undefined
            };

            const response = await axiosHttp.post(`/v2/ai/generate`, payload);

            if (response.data && response.data.status) {
                const normalized = response.data.results?.normalized;
                let finalJsonStr = "";

                if (normalized && typeof normalized === "object") {
                    finalJsonStr = JSON.stringify(normalized, null, 2);
                } else {
                    const cfResult = response.data.results?.result;
                    const resData = response.data.results || {};

                    let rawText =
                        (typeof cfResult === "string") ? cfResult :
                        (cfResult?.response ||
                            cfResult?.output ||
                            cfResult?.text ||
                            cfResult?.message?.content ||
                            cfResult?.choices?.[0]?.message?.content ||
                            cfResult?.choices?.[0]?.message?.reasoning_content ||
                            resData.response);

                    if (!rawText && typeof cfResult === "object" && cfResult?.choices?.[0]?.finish_reason === "length") {
                        rawText = "⚠️ AI generation was truncated (hit max tokens limit) before it could produce the final JSON. Please try again or increase max_tokens.";
                    } else if (!rawText) {
                        rawText = "";
                    }

                    if (typeof rawText !== "string") {
                        try { rawText = JSON.stringify(rawText); } catch (e) { rawText = String(rawText); }
                    }

                    // Backward-compatible fallback for servers without normalized output.
                    finalJsonStr = rawText.trim();
                    const firstBrace = finalJsonStr.indexOf("{");
                    const lastBrace = finalJsonStr.lastIndexOf("}");
                    if (firstBrace !== -1 && lastBrace !== -1 && lastBrace >= firstBrace) {
                        finalJsonStr = finalJsonStr.substring(firstBrace, lastBrace + 1);
                    }

                    try {
                        const parsedObj = JSON.parse(finalJsonStr);
                        finalJsonStr = JSON.stringify(parsedObj, null, 2);
                    } catch (e) {}
                }

                if (finalJsonStr) {
                    setGeneratedText(finalJsonStr);
                    setHasGenerated(true);
                    savePromptToHistory(finalPrompt);
                    message.success("Generated successfully!");
                }
            } else {
                message.error(response.data?.message || "Failed to generate content.");
            }
        } catch (err) {
            console.error("AI Generation Error:", err);
            message.error("An error occurred during generation.");
        } finally {
            setIsGenerating(false);
        }
    };

    const handleApply = () => {
        if (onApply) {
            try {
                const parsed = JSON.parse(generatedText);
                onApply(parsed);
            } catch (e) {
                if (mode === "single") {
                    // Be forgiving: If AI returned plain text instead of JSON, 
                    // assume the plain text represents the target field exactly.
                    onApply({ [fieldName]: generatedText });
                } else {
                    message.error("Generated text is not valid JSON. Cannot apply.");
                    return;
                }
            }
        }
        setIsModalVisible(false);
    };

    const historyOptions = promptHistory.map(p => ({ value: p }));

    const getCharCounts = (text) => {
        if (!text) return null;
        try {
            const parsed = JSON.parse(text);
            let counts = {};
            
            const metaArray = Array.isArray(parsed.meta) ? parsed.meta : (Array.isArray(parsed) ? parsed : []);
            if (metaArray.length > 0) {
                metaArray.forEach(item => {
                    const key = (item.name || item.property || item.httpEquiv);
                    if (key && typeof item.content === 'string') {
                        counts[key.toLowerCase()] = item.content.trim().length;
                    }
                });
            } else {
                Object.entries(parsed).forEach(([k, v]) => {
                    if (typeof v === 'string') counts[k.toLowerCase()] = v.trim().length;
                });
            }
            return counts;
        } catch(e) {
            return null;
        }
    };

    const charCounts = generatedText && !isGenerating ? getCharCounts(generatedText) : null;

    return (
        <>
            {buttonComponent ? (
                <div onClick={() => setIsModalVisible(true)} style={{ display: 'inline-block' }}>
                    {buttonComponent}
                </div>
            ) : (
                <Tooltip title={`Generate ${fieldLabel} with AI`} placement="top">
                    <Button
                        onClick={() => setIsModalVisible(true)}
                        style={{
                            width: 38,
                            height: 38,
                            padding: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: 6,
                            border: "1px solid #d9d9d9",
                            background: "linear-gradient(135deg, #f0f5ff 0%, #f0fdf4 100%)",
                            boxShadow: "0 2px 0 rgba(0, 0, 0, 0.015)"
                        }}
                    >
                        <svg className="sparkle-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z" 
                                    fill={`url(#sparkleGrad_${fieldName || 'bulk'})`} />
                            <path d="M19 2L19.75 4.25L22 5L19.75 5.75L19 8L18.25 5.75L16 5L18.25 4.25L19 2Z" 
                                    fill={`url(#sparkleGrad2_${fieldName || 'bulk'})`} opacity="0.8" />
                            <path d="M6 16L6.5 17.5L8 18L6.5 18.5L6 20L5.5 18.5L4 18L5.5 17.5L6 16Z" 
                                    fill={`url(#sparkleGrad2_${fieldName || 'bulk'})`} opacity="0.6" />
                            <defs>
                                <linearGradient id={`sparkleGrad_${fieldName || 'bulk'}`} x1="4" y1="2" x2="20" y2="18" gradientUnits="userSpaceOnUse">
                                    <stop stopColor="#3B82F6" />
                                    <stop offset="1" stopColor="#10B981" />
                                </linearGradient>
                                <linearGradient id={`sparkleGrad2_${fieldName || 'bulk'}`} x1="16" y1="2" x2="22" y2="8" gradientUnits="userSpaceOnUse">
                                    <stop stopColor="#6366F1" />
                                    <stop offset="1" stopColor="#14B8A6" />
                                </linearGradient>
                            </defs>
                        </svg>
                    </Button>
                </Tooltip>
            )}

            <Modal
                title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: '32px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{
                                width: '28px',
                                height: '28px',
                                background: 'linear-gradient(135deg, #6366F1 0%, #A855F7 100%)',
                                borderRadius: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 2px 4px rgba(99, 102, 241, 0.2)'
                            }}>
                                <ThunderboltOutlined style={{ color: 'white', fontSize: '14px' }} />
                            </div>
                            <span style={{ fontSize: '16px', fontWeight: 600, letterSpacing: '-0.01em' }}>
                                {mode === "bulk" ? (
                                    <>Bulk Generate <span style={{ color: '#6366F1' }}>Meta Tags</span></>
                                ) : (
                                    <>Generate <span style={{ color: '#6366F1' }}>{fieldLabel}</span></>
                                )}
                            </span>
                        </div>
                        <Tooltip title="Global SEO Settings">
                            <Button 
                                type="text" 
                                icon={<SettingOutlined />} 
                                onClick={() => setConfigVisible(true)}
                                style={{ color: '#9ca3af', borderRadius: '50%' }}
                            />
                        </Tooltip>
                    </div>
                }
                open={isModalVisible}
                onCancel={() => !isGenerating && setIsModalVisible(false)}
                footer={null}
                width={620}
                centered
                destroyOnClose
                style={{ borderRadius: '12px', overflow: 'hidden' }}
                styles={{
                    content: { borderRadius: '12px' },
                    header: { padding: '20px 24px 16px', borderBottom: '1px solid #f1f5f9' },
                    body: { padding: '24px' }
                }}
            >
                <style>{pulseKeyframes}</style>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    
                    {/* Prompt Section */}
                    <div>
                        {activeMetaKeys.length > 0 && (
                            <div style={{ marginBottom: '16px' }}>
                                <div style={{ 
                                    display: 'flex', 
                                    alignItems: 'center',
                                    marginBottom: '8px' 
                                }}>
                                    <span style={{ fontWeight: 600, fontSize: '13px', color: '#374151' }}>
                                        Target SEO Fields
                                    </span>
                                </div>
                                <Select
                                    mode="multiple"
                                    style={{ width: '100%' }}
                                    placeholder="Select SEO fields to generate"
                                    value={selectedFields}
                                    onChange={setSelectedFields}
                                    disabled={isGenerating || mode === "single"}
                                    options={activeMetaKeys.map(key => ({
                                        label: key.charAt(0).toUpperCase() + key.slice(1),
                                        value: key
                                    }))}
                                />
                            </div>
                        )}

                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            marginBottom: '8px' 
                        }}>
                            <span style={{ fontWeight: 600, fontSize: '13px', color: '#374151' }}>
                                AI Focus & Context
                            </span>
                        </div>
                        <div style={{ 
                            display: 'flex', 
                            gap: '12px',
                            background: '#f8fafc',
                            padding: '12px',
                            borderRadius: '10px',
                            border: '1px solid #e2e8f0'
                        }}>
                            <AutoComplete
                                options={historyOptions}
                                value={inlinePrompt}
                                onChange={val => setInlinePrompt(val)}
                                onSelect={val => setInlinePrompt(val)}
                                disabled={isGenerating}
                                style={{ flex: 1 }}
                            >
                                <Input 
                                    value={inlinePrompt}
                                    onChange={e => setInlinePrompt(e.target.value)}
                                    placeholder={`E.g., Generate compelling SEO content...`} 
                                    onPressEnter={handleGenerate}
                                    style={{ 
                                        borderRadius: '8px', 
                                        border: '1px solid #cbd5e1',
                                        padding: '8px 12px',
                                        color: '#1f2937'
                                    }}
                                />
                            </AutoComplete>
                            <Button 
                                type="primary" 
                                onClick={handleGenerate} 
                                loading={isGenerating}
                                icon={!isGenerating && <SyncOutlined />}
                                style={{
                                    height: '40px',
                                    borderRadius: '8px',
                                    padding: '0 20px',
                                    background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                                    border: 'none',
                                    boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.2)',
                                    fontWeight: 500
                                }}
                            >
                                {hasGenerated ? "Regenerate" : "Generate"}
                            </Button>
                        </div>
                    </div>

                    {/* Output Section */}
                    <div>
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            marginBottom: '8px' 
                        }}>
                            <span style={{ fontWeight: 600, fontSize: '13px', color: '#374151' }}>
                                Result Preview
                            </span>
                            {generatedText && (
                                <Button 
                                    type="text" 
                                    size="small" 
                                    onClick={() => {
                                        navigator.clipboard.writeText(generatedText);
                                        message.success("Copied to clipboard!");
                                    }}
                                    style={{ fontSize: '11px', color: '#64748b' }}
                                >
                                    Copy
                                </Button>
                            )}
                        </div>
                        <div style={{ position: 'relative' }}>
                            <TextArea
                                rows={8}
                                value={isGenerating ? "AI is processing your request...\n\nAnalyzing course data...\nGenerating optimized SEO content..." : generatedText}
                                onChange={(e) => setGeneratedText(e.target.value)}
                                placeholder="AI JSON output will appear here..."
                                disabled={isGenerating}
                                style={{ 
                                    resize: 'none',
                                    borderRadius: '12px',
                                    padding: '16px',
                                    fontSize: '14px',
                                    lineHeight: '1.6',
                                    border: '1px solid #e2e8f0',
                                    backgroundColor: isGenerating ? '#f8fafc' : '#fff',
                                    color: isGenerating ? '#94a3b8' : '#1e293b',
                                    fontWeight: isGenerating ? 500 : 400,
                                    animation: isGenerating ? 'aiPulse 2s infinite ease-in-out' : 'none',
                                    transition: 'all 0.3s ease',
                                    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)'
                                }}
                            />
                            {isGenerating && (
                                <div style={{ 
                                    position: 'absolute', 
                                    bottom: '12px', 
                                    right: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}>
                                    <div className="dot-flashing" />
                                    <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 500 }}>Thinking...</span>
                                </div>
                            )}
                        </div>
                        {charCounts && Object.keys(charCounts).length > 0 && (
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px' }}>
                                {Object.entries(charCounts).map(([key, count]) => {
                                    const max = key.includes('title') ? 60 : 160;
                                    const isWarning = count > max;
                                    return (
                                        <div key={key} style={{
                                            fontSize: '12px',
                                            padding: '4px 10px',
                                            borderRadius: '6px',
                                            backgroundColor: isWarning ? '#fff1f0' : '#f8fafc',
                                            border: `1px solid ${isWarning ? '#ffa39e' : '#e2e8f0'}`,
                                            color: isWarning ? '#cf1322' : '#475569'
                                        }}>
                                            <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{key}</span>: {count} chars 
                                            {isWarning && ' ⚠️'}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Footer Actions */}
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'flex-end', 
                        gap: '12px', 
                        marginTop: '8px',
                        paddingTop: '16px',
                        borderTop: '1px solid #f1f5f9'
                    }}>
                        <Button 
                            onClick={() => setIsModalVisible(false)} 
                            disabled={isGenerating}
                            style={{ 
                                borderRadius: '8px',
                                height: '40px',
                                padding: '0 24px'
                            }}
                        >
                            Cancel
                        </Button>
                        <Button 
                            type="primary" 
                            onClick={handleApply} 
                            disabled={isGenerating || !generatedText}
                            icon={<CheckOutlined />}
                            style={{ 
                                borderRadius: '8px',
                                height: '40px',
                                padding: '0 24px',
                                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                                border: 'none',
                                boxShadow: '0 4px 6px -1px rgba(16, 185, 129, 0.2)',
                                fontWeight: 600
                            }}
                        >
                            Use This Content
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Global Settings Modal */}
            <AIGeneratorConfig
                visible={configVisible}
                onClose={() => setConfigVisible(false)}
                category={category}
                defaultPrompt="You are an expert SEO copywriter and marketer. Your goal is to generate compelling, accurate, and concise content."
                contextInstructions="Generate SEO meta content from course context. Output format and keys are enforced by runtime contract."
                onSaved={handleSettingsSaved}
            />
        </>
    );
};

export default SingleFieldAIGenerator;
